// Daily Crowns leaderboard, shared with the Crowns app.
//
// Same backend as the app (lib/utils/leaderboard_helper.dart): the Spark &
// Spiral Supabase `scores` table, one board per (game, level_number), where
// level_number is the day of the year and time is in hundredths of a second.
//
//   - App players post to `crowns_daily`, and the app reads only that.
//   - Web players post to `crowns_daily_web`, which no app reads, so a web time
//     can never reach the app boards.
//   - The web board shows both, with web times tagged.
//
// A web player is a `users` row keyed by a random device id, the same way the
// app identifies an install. It's kept in one synced key (PLAYER_KEY), so a
// signed-in player has the same name in every browser and can change it on
// the account page. Plain fetch with the public anon key, so pages using this
// don't load supabase-js.
import { SPARK_SUPABASE_URL, SPARK_SUPABASE_ANON_KEY } from '../../account/sparkProject';
import { isCleanName } from '../../utils/nameFilter';

export const APP_GAME = 'crowns_daily';
export const WEB_GAME = 'crowns_daily_web';
export const BOARD_SIZE = 30;

// "v1|<device id>|<user id>|<name>". A plain string, not JSON, so when two
// browsers each made a player before signing in, progressSync keeps one whole
// player instead of merging the two field by field.
const PLAYER_KEY = 'crowns-puzzle-lb-player';
// Synced with the player's account (progressSync), so a signed-in player who
// posted on one computer doesn't post again from another.
const postedKey = (dateKey) => `crowns-puzzle-lb-posted-${dateKey}`;

const REST = `${SPARK_SUPABASE_URL}/rest/v1`;
const BOTH = `in.(${APP_GAME},${WEB_GAME})`;

const local = {
  get(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
  },
};

function readPlayer() {
  let raw = null;
  try { raw = localStorage.getItem(PLAYER_KEY); } catch (e) { /* ignore */ }
  const [v, deviceId, id, ...name] = (raw || '').split('|');
  if (v !== 'v1' || !deviceId) return null;
  return { deviceId, id: Number(id) || null, name: name.join('|') };
}

function writePlayer({ deviceId, id, name }) {
  try { localStorage.setItem(PLAYER_KEY, ['v1', deviceId, id || '', name || ''].join('|')); } catch (e) { /* ignore */ }
}

function deviceId() {
  const player = readPlayer();
  if (player) return player.deviceId;
  const id = crypto.randomUUID();
  writePlayer({ deviceId: id });
  return id;
}

async function api(path, { method = 'GET', body, prefer, device = false } = {}) {
  const headers = {
    apikey: SPARK_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SPARK_SUPABASE_ANON_KEY}`,
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (prefer) headers.Prefer = prefer;
  if (device) headers['x-device-id'] = deviceId();
  const res = await fetch(`${REST}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (!res.ok) {
    const err = new Error(`leaderboard ${method} ${res.status}`);
    try { err.code = (await res.json()).code; } catch (e) { /* ignore */ }
    throw err;
  }
  return res;
}

async function count(query) {
  const res = await api(`/scores?select=id&${query}`, { method: 'HEAD', prefer: 'count=exact' });
  const range = res.headers.get('content-range') || '';
  return Number(range.split('/')[1]) || 0;
}

// ---- player -----------------------------------------------------------------
/** { id, name } once this player has a leaderboard name, else null. */
export function savedProfile() {
  const player = readPlayer();
  return player?.id ? { id: player.id, name: player.name } : null;
}

/** Checks a leaderboard name with the app's rules. Returns an error or ''. */
export function nameError(raw) {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < 3) return 'Use at least 3 characters.';
  if (name.length > 25) return 'Use 25 characters or fewer.';
  if (!/^[a-zA-Z0-9_ ]+$/.test(name)) return 'Use letters, numbers, spaces and _ only.';
  if (!isCleanName(name)) return 'Please choose a different name.';
  return '';
}

const ADJECTIVES = ['Swift', 'Clever', 'Mighty', 'Silent', 'Wise', 'Brave', 'Gentle', 'Fierce', 'Noble', 'Calm', 'Wild', 'Proud', 'Loyal', 'Quick', 'Bright'];
const ANIMALS = ['Fox', 'Owl', 'Bear', 'Wolf', 'Hawk', 'Lynx', 'Deer', 'Hare', 'Frog', 'Bat', 'Mole', 'Dove', 'Swan', 'Crab', 'Bee'];
const pick = (list) => list[Math.floor(Math.random() * list.length)];
/** The app's name for players who skip choosing one. */
export const anonymousName = () => `Anonymous ${pick(ADJECTIVES)} ${pick(ANIMALS)}`;

/** Creates (or renames) this browser's leaderboard player. */
export async function saveName(name) {
  const display = name.trim().replace(/\s+/g, ' ');
  const profile = savedProfile();
  if (profile?.id) {
    const res = await api(`/users?id=eq.${profile.id}&select=id`, {
      method: 'PATCH', device: true, prefer: 'return=representation',
      body: { display_name: display, updated_at: new Date().toISOString() },
    });
    if ((await res.json()).length) {
      writePlayer({ deviceId: deviceId(), id: profile.id, name: display });
      return savedProfile();
    }
  }
  // An insert for a device id that already exists returns the existing row.
  const res = await api('/users?select=id,display_name', {
    method: 'POST', device: true, prefer: 'return=representation',
    body: { device_id: deviceId(), display_name: display },
  });
  const [row] = await res.json();
  if (!row?.id) throw new Error('leaderboard: no player id');
  if (row.display_name !== display) {
    await api(`/users?id=eq.${row.id}`, {
      method: 'PATCH', device: true, body: { display_name: display, updated_at: new Date().toISOString() },
    });
  }
  writePlayer({ deviceId: deviceId(), id: row.id, name: display });
  return savedProfile();
}

// ---- scores -----------------------------------------------------------------
export const hasPosted = (dateKey) => Boolean(local.get(postedKey(dateKey)));

const inFlight = new Map();

/** Posts today's time once per day (first solve only, like the app). */
export function postTime(dateKey, board, seconds) {
  const profile = savedProfile();
  if (!profile?.id || hasPosted(dateKey)) return Promise.resolve();
  // One request even if the board mounts twice while it's in flight.
  if (!inFlight.has(dateKey)) {
    inFlight.set(dateKey, (async () => {
      try {
        await api('/scores', {
          method: 'POST', prefer: 'return=minimal',
          body: { level_number: board, time: Math.round(seconds * 100), user_id: profile.id, game: WEB_GAME },
        });
      } catch (e) {
        // 23514: under the server's 3 second minimum. Don't retry.
        if (e.code !== '23514') throw e;
      }
      local.set(postedKey(dateKey), true);
    })().finally(() => inFlight.delete(dateKey)));
  }
  return inFlight.get(dateKey);
}

/** The web board (app + web) for `board`, plus where `seconds` ranks on it. */
export async function loadBoard(board, seconds) {
  const level = `level_number=eq.${board}`;
  const res = await api(`/scores?select=time,user_id,game,users!inner(display_name)&game=${BOTH}&${level}&order=time.asc,created_at.asc&limit=${BOARD_SIZE}`);
  const rows = (await res.json()).map((r) => ({
    userId: r.user_id, name: r.users?.display_name || 'Player', time: r.time, web: r.game === WEB_GAME,
  }));
  const cs = Math.round(seconds * 100);
  const [total, ahead] = await Promise.all([
    count(`game=${BOTH}&${level}`),
    count(`game=${BOTH}&${level}&time=lt.${cs}`),
  ]);
  // Rank = 1 + players strictly faster, so ties share a rank (as in the app).
  return { rows, total, rank: ahead + 1 };
}
